import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ReactRelayContext } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { getExtensionRenderer } from '@atlaskit/editor-common/utils';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';

import { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality/data-source-provider';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { getConnectDataListener } from '@confluence/app-referentiality/entry-points/connectDataListener.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { createNestedLcmHydrationExtensionHandlers } from '@confluence/content-renderer-legacy-macros/entry-points/createNestedLcmHydrationExtensionHandlers.ts';
import { getSsrExtensionHandlers } from '@confluence/content-renderer-legacy-macros/entry-points/getSsrExtensionHandlers.ts';
import { removeDuplicateResourcesForMacros } from '@confluence/content-renderer-legacy-macros/entry-points/ssrTransformers.ts';
import type { ContentUnifiedQueryMacroRenderedOutputType } from '@confluence/content-unified-query';
import {
	EXTENSION_KEY,
	EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { createPreloadedStaticMacroHandlers } from '@confluence/forge-ui/entry-points/createPreloadedStaticMacroHandlers.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { includeSuperbatchForFabricMacro } from '../PageContentRenderer.tsx';

import { createExtensionHandlersWithoutSSR } from './createExtensionHandlersWithoutSSR.ts';
import { createLiveSearchHandoffExtensionHandlers } from './createLiveSearchHandoffExtensionHandlers.ts';
import type { PageContentRendererFabricProps } from './PageContentRendererFabricProps.ts';

const TEAM_CALENDAR_EXTENSION_KEY_REGEXP = new RegExp(
	`"extensionKey"\\s*:\\s*"${EXTENSION_KEY.TEAM_CALENDARS}"`,
);
const LEGACY_CONTENT_EXTENSION_KEY_REGEXP = new RegExp(
	`"extensionKey"\\s*:\\s*"${EXTENSION_KEY.LEGACY_CONTENT}"`,
);
const LIVE_SEARCH_EXTENSION_KEY_REGEXP = new RegExp(
	`"extensionKey"\\s*:\\s*"${EXTENSION_KEY.LIVE_SEARCH}"`,
);

export function useExtensionHandlersForPageContentRendererFabric(
	{
		content,
		contentId,
		contentBody,
		contentMode,
		isTemplateEditorRenderer,
		spaceKey,
		spaceId,
		isPreviewMode,
		revision,
	}: PageContentRendererFabricProps,
	annotationProvider: ADFRendererProps['annotationProvider'],
): {
	dataProviders: ProviderFactory;
	extensionHandlers: ExtensionHandlers<any> | undefined;
} {
	const { featureFlags, environment, cloudId, userId, locale, activationId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [manifestReadyState, setManifestReadyState] = useState(false);
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);
	const isLivePage = useIsCurrentPageLive();

	const contentType = content?.type;
	const adf = contentBody?.value || '';
	const hasTeamCalendarMacro = useMemo(() => TEAM_CALENDAR_EXTENSION_KEY_REGEXP.test(adf), [adf]);
	const hasLegacyContentMacro = useMemo(() => LEGACY_CONTENT_EXTENSION_KEY_REGEXP.test(adf), [adf]);
	const hasLiveSearchMacro = useMemo(() => LIVE_SEARCH_EXTENSION_KEY_REGEXP.test(adf), [adf]);

	const hasExcerptMacro = useMemo(() => /"extensionKey"\s*:\s*"excerpt"/.test(adf), [adf]);
	const preserveExcerptRenderer =
		hasExcerptMacro &&
		Boolean(window.__SSR_RENDERED__) &&
		expValEquals('cc_excerpt_nested_renderer_cls', 'isEnabled', true);

	const PDF_URL = '/wiki/pdf/';
	const isCurrentLocationPdf = (): boolean => Boolean(window?.location?.href?.includes(PDF_URL));

	const contentStatus = isCurrentLocationPdf() ? content?.status : undefined;

	const macroRenderedOutput = useMemo(
		() =>
			content && 'macroRenderedOutput' in content
				? removeDuplicateResourcesForMacros(
						content['macroRenderedOutput'] as ContentUnifiedQueryMacroRenderedOutputType[],
					)
				: null,
		[content],
	);

	// If legacy fabric macros exist, superbatch css and js needs to be added
	// only once before any macros are rendered.
	const includeSuperbatch = useCallback(() => {
		includeSuperbatchForFabricMacro(contentBody, macroRenderedOutput);
	}, [contentBody, macroRenderedOutput]);

	// Extension handlers during SSR should not be used to populate extension providers
	// Otherwise extensions manifest would reference the wrong handlers if it needs to be overrided
	// during runtime
	const extensionHandlersWithSSR = useMemo(
		() =>
			getSsrExtensionHandlers({
				adf: contentBody?.value,
				contentId: contentId || '',
				contentMode,
				spaceKey,
				cloudId,
				userId,
				macroRenderedOutput,
				includeSuperbatch,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentMode, includeSuperbatch],
	);

	// The SSR preload is only current for the content and revision this renderer hydrated with.
	// PDF exports and previews are excluded: they query static macros in a different mode or for a draft.
	const isSsrOrHydrating = useIsSSROrHydrating();
	const [ssrPreloadedContent] = useState(() =>
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		isSsrOrHydrating && fg('conf_frontend_static_macro_perf') ? { contentId, revision } : undefined,
	);
	const [hasLeftSsrPreloadedContent, setHasLeftSsrPreloadedContent] = useState(false);
	const isOnSsrPreloadedContent =
		!!ssrPreloadedContent &&
		!hasLeftSsrPreloadedContent &&
		ssrPreloadedContent.contentId === contentId &&
		ssrPreloadedContent.revision === revision;
	if (ssrPreloadedContent && !hasLeftSsrPreloadedContent && !isOnSsrPreloadedContent) {
		// Stays off if the renderer later returns to this content: the preload may be stale by then.
		setHasLeftSsrPreloadedContent(true);
	}

	const relayEnvironment = useContext(ReactRelayContext)?.environment;
	const canUsePreloadedStaticMacros =
		isOnSsrPreloadedContent && !isPreviewMode && !isCurrentLocationPdf();
	const preloadedStaticMacroHandlers = useMemo(
		() =>
			canUsePreloadedStaticMacros
				? createPreloadedStaticMacroHandlers({
						contentId: contentId || '',
						cloudId,
						relayEnvironment,
					})
				: undefined,
		[canUsePreloadedStaticMacros, contentId, cloudId, relayEnvironment],
	);

	const extensionHandlersWithPreloadedStaticMacros = useMemo(
		() =>
			preloadedStaticMacroHandlers
				? { ...extensionHandlersWithSSR, ...preloadedStaticMacroHandlers }
				: extensionHandlersWithSSR,
		[extensionHandlersWithSSR, preloadedStaticMacroHandlers],
	);
	// Only pages that actually contain an LCM can be affected by recursive LCM SSR, so the gate is
	// read behind that check: an LCM-free page must not be counted as exposed to it.
	const nestedLcmHydrationExtensionHandlers = useMemo(
		() =>
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			hasLegacyContentMacro && fg('confluence_lcm_ssr_nested')
				? createNestedLcmHydrationExtensionHandlers({
						annotationProvider,
						contentId: contentId || '',
						contentMode,
						// Same macro rendering as the server, but declining the macros the SSR path does
						// not own so they reach their manifests instead of staying empty forever.
						nestedExtensionHandlers: getSsrExtensionHandlers({
							adf: contentBody?.value,
							contentId: contentId || '',
							contentMode,
							spaceKey,
							cloudId,
							userId,
							macroRenderedOutput,
							includeSuperbatch,
							releaseEmptyMacrosToManifest: true,
						}),
						serverExtensionHandlers: extensionHandlersWithSSR,
					})
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			annotationProvider,
			contentId,
			contentMode,
			extensionHandlersWithSSR,
			hasLegacyContentMacro,
			includeSuperbatch,
		],
	);

	const excerptHydrationHandlers = useMemo(
		() =>
			preserveExcerptRenderer
				? getSsrExtensionHandlers({
						adf: contentBody?.value,
						annotationProvider,
						contentId: contentId || '',
						contentMode,
						spaceKey,
						cloudId,
						userId,
						macroRenderedOutput,
						includeSuperbatch,
						releaseEmptyMacrosToManifest: true,
					})
				: undefined,
		[
			preserveExcerptRenderer,
			annotationProvider,
			contentBody?.value,
			contentId,
			contentMode,
			spaceKey,
			cloudId,
			userId,
			macroRenderedOutput,
			includeSuperbatch,
		],
	);

	const referentialityDataSourceProvider = useMemo(() => {
		const refDataProvider = new ReferentialityDataSourceProvider();

		getConnectDataListener().init({
			emit: (localId, reference) => refDataProvider.createOrUpdate(localId, reference),
		});

		return refDataProvider;
	}, []);

	const extensionHandlers = useMemo(() => {
		return getExtensionHandlers?.(
			{
				adf,
				contentId,
				spaceKey,
				macroRenderedOutput,
				showTemplateVariablesInPreview: isTemplateEditorRenderer,
				shouldIncludeMacroWithReferencesHandlers: true,
				referentialityDataSourceProvider,
				contentType,
				isPreviewMode,
				annotationProvider,
			},
			createAnalyticsEvent,
		) as ADFRendererProps['extensionHandlers'];
	}, [
		getExtensionHandlers,
		contentId,
		spaceKey,
		adf,
		macroRenderedOutput,
		contentType,
		isPreviewMode,
		createAnalyticsEvent,
		isTemplateEditorRenderer,
		annotationProvider,
		referentialityDataSourceProvider,
	]);

	const isSsrRendered = window.__SSR_RENDERED__;
	// The extensionHandlers prop value of PageContentRendererFabric when NOT isTemplateEditorRenderer:
	const handlers: ADFRendererProps['extensionHandlers'] = useMemo(() => {
		if (!isTemplateEditorRenderer) {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				// Return extension handlers for supported content only
				return extensionHandlersWithPreloadedStaticMacros;
			} else if (!manifestReadyState) {
				// POST/NON SSR, macros V2 is on, and manifest is ready, extension handlers is undefined to use extension manifest
				// Don't use client side macro renderer until manifestReadyState because it takes additional cycle to render
				// The loading state will destroy the SSR rendered content
				//
				// The first render of client-side need extensionHandlersWithSSR for proper hydration as well.
				if (isSsrRendered) {
					return extensionHandlersWithPreloadedStaticMacros;
				} else {
					return createExtensionHandlersWithoutSSR(extensionHandlers);
				}
			} else {
				// After the extension manifest is ready, most macros return undefined so the manifest
				// renderer can take over. Some families stay on the product handler instead: migrated
				// Team Calendar macros, so the sized SSR placeholder survives the manifest-ready
				// handoff; recursively SSRed LCM hierarchies, so the mounted component tree
				// survives it; and Live Search, so input typed while the page loads is not lost to a
				// remount.
				const lcmHandlers =
					hasLegacyContentMacro && isSsrRendered ? nestedLcmHydrationExtensionHandlers : undefined;
				// Team Calendar remains on the MACRO_CORE handler after manifest readiness, but sibling native
				// embeds must fall through to their manifests or native databases will not render.
				const teamCalendarHandlers =
					hasTeamCalendarMacro &&
					expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)
						? createExtensionHandlersWithoutSSR(extensionHandlers, {
								allowNativeEmbedHandling: false,
							})
						: undefined;
				// Live Search shares the macro-core type with Team Calendar, so it wraps the Team
				// Calendar set rather than replacing it.
				const macroCoreHandlers =
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					hasLiveSearchMacro && isSsrRendered && fg('confluence_live_search_ssr_input')
						? createLiveSearchHandoffExtensionHandlers(
								extensionHandlersWithPreloadedStaticMacros,
								teamCalendarHandlers,
							)
						: teamCalendarHandlers;

				// Preserve the disjoint Forge, LCM, Calendar and Live Search handler sets before adding Excerpt.
				const staticMacroHandlers = preloadedStaticMacroHandlers;
				const retainedHandlers =
					!staticMacroHandlers && (!lcmHandlers || !macroCoreHandlers)
						? (lcmHandlers ?? macroCoreHandlers)
						: { ...macroCoreHandlers, ...lcmHandlers, ...staticMacroHandlers };
				if (!excerptHydrationHandlers) {
					return retainedHandlers;
				}
				const renderExcerpt = getExtensionRenderer(
					excerptHydrationHandlers[EXTENSION_TYPE.MACRO_CORE],
				);
				const existingCore = retainedHandlers?.[EXTENSION_TYPE.MACRO_CORE];
				const renderExisting = existingCore ? getExtensionRenderer(existingCore) : undefined;
				// Keep the same Excerpt/ADF tree mounted. Its releasing nested handlers still
				// hand unsupported and interactive macros to their manifests.
				return {
					...retainedHandlers,
					[EXTENSION_TYPE.MACRO_CORE]: (extension, ...args) =>
						extension.extensionKey === EXTENSION_KEY.EXCERPT
							? renderExcerpt(extension, ...args)
							: renderExisting
								? renderExisting(extension, ...args)
								: null,
				};
			}
		}
	}, [
		extensionHandlers,
		extensionHandlersWithPreloadedStaticMacros,
		excerptHydrationHandlers,
		hasLegacyContentMacro,
		hasLiveSearchMacro,
		hasTeamCalendarMacro,
		isSsrRendered,
		isTemplateEditorRenderer,
		manifestReadyState,
		nestedLcmHydrationExtensionHandlers,
		preloadedStaticMacroHandlers,
	]);

	const { dataProviders, manifestPreload } = useMemo<{
		dataProviders: ProviderFactory;
		manifestPreload?: Promise<void>;
	}>(
		() => {
			const providerFactory = ProviderFactory.create({});
			const intlStub = {
				formatMessage: (message) => message?.defaultMessage,
				locale,
			};
			let manifestPreload: Promise<void> | undefined;

			if (populateExtensionProviders) {
				// The effect below subscribes to the returned preload, so a render React discards
				// cannot flip the manifest-ready state.
				manifestPreload =
					populateExtensionProviders(providerFactory, {
						intl: intlStub,
						containerId: spaceKey,
						accountId: userId,
						cloudId,
						spaceKey,
						spaceId,
						contentId,
						contentType,
						contentVersion: content?.version?.number,
						contentStatus,
						contentMode,
						environment,
						featureFlags,
						extensionHandlers,
						createAnalyticsEvent,
						activationId,
						isLivePage,
						hasLegacyContentMacro,
					}) || undefined;
			}

			return { dataProviders: providerFactory, manifestPreload };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			contentMode,
			contentType,
			extensionHandlers,
			populateExtensionProviders,
			createAnalyticsEvent,
			isLivePage,
			hasLegacyContentMacro,
		],
	);

	useEffect(() => {
		if (!manifestPreload) {
			return;
		}
		let cancelled = false;
		void manifestPreload.then(
			() => {
				if (!cancelled) {
					setManifestReadyState(true);
				}
			},
			() => undefined,
		);
		return () => {
			cancelled = true;
		};
	}, [manifestPreload]);

	return {
		dataProviders,
		extensionHandlers: isTemplateEditorRenderer ? extensionHandlers : handlers,
	};
}
